import { motion } from "framer-motion";
import { Check, CalendarClock, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow, PrimaryCTA, Avatar, Chip } from "../components/bits";
import { CALENDLY_URL } from "../data/content";

const ease = [0.22, 1, 0.36, 1];
const CALL_STEPS = ["Discover", "Identify opportunities", "Build the system", "Grow"];

export const Discovery = () => (
    <section data-testid="discovery-call" className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <div className="grid items-center gap-12 rounded-3xl border border-linen bg-ivory p-7 sm:p-10 lg:grid-cols-2 lg:gap-16 lg:p-14">
                <div>
                    <Eyebrow>Discovery call / 30 minutes</Eyebrow>
                    <h2 className="mt-4 font-serif text-3xl uppercase leading-[1.08] tracking-tight text-ink sm:text-4xl lg:text-5xl">
                        Let’s talk.
                    </h2>
                    <p className="mt-4 max-w-md text-sm leading-relaxed text-mute sm:text-base">
                        Book a free 30-minute discovery call and explore where AI could create the biggest impact
                        in your business.
                    </p>
                    <div className="mt-8">
                        <PrimaryCTA testId="discovery-book-btn">Book a Free Discovery Call</PrimaryCTA>
                    </div>
                    <div className="mt-7 flex items-center gap-3 font-mono text-[9px] uppercase tracking-[0.2em] text-faint">
                        <span className="h-1.5 w-1.5 rounded-full bg-rose" />
                        Free · 30 minutes · No obligation
                    </div>
                </div>

                <div className="relative">
                    <div className="grid gap-3 sm:grid-cols-[auto_1fr] sm:gap-6">
                        <div className="rounded-2xl border border-linen bg-white p-5 shadow-[0_12px_40px_rgba(17,17,19,0.06)]">
                            <div className="flex items-center justify-between">
                                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-faint">Calendar</span>
                                <CalendarClock className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
                            </div>
                            <div className="mt-3 grid grid-cols-7 gap-1">
                                {Array.from({ length: 21 }).map((_, i) => (
                                    <motion.span
                                        key={i}
                                        animate={i === 9 ? { scale: [1, 1.18, 1] } : {}}
                                        transition={{ duration: 2.4, repeat: Infinity, ease }}
                                        className={`flex aspect-square items-center justify-center rounded font-mono text-[7px] ${
                                            i === 9 ? "bg-ink text-cream ring-1 ring-gold" : "bg-sand/70 text-ink/40"
                                        }`}
                                    >
                                        {i + 1}
                                    </motion.span>
                                ))}
                            </div>
                            <div className="mt-3 flex items-center justify-center gap-1.5 rounded-lg bg-blush py-2">
                                <Check className="h-3 w-3 text-ink" strokeWidth={2.5} />
                                <span className="font-mono text-[8px] uppercase tracking-[0.18em] text-ink">30 minutes · confirmed</span>
                            </div>
                        </div>
                        <div className="relative flex flex-col justify-center gap-0">
                            {CALL_STEPS.map((t, i) => (
                                <div key={t} className="flex items-center gap-3.5">
                                    <div className="flex flex-col items-center">
                                        <motion.span
                                            animate={{ borderColor: ["#E8E2D9", "#CBA368", "#E8E2D9"] }}
                                            transition={{ duration: 3, repeat: Infinity, delay: i * 0.6 }}
                                            className={`flex h-8 w-8 items-center justify-center rounded-full border-2 bg-white font-mono text-[9px] ${i === 3 ? "text-gold" : "text-ink/60"}`}
                                        >
                                            {i === 3 ? <ArrowUpRight className="h-3.5 w-3.5" /> : String(i + 1).padStart(2, "0")}
                                        </motion.span>
                                        {i < 3 && <span className="h-5 w-px bg-rose/40" />}
                                    </div>
                                    <span className="font-sans text-[12px] font-medium uppercase tracking-[0.12em] text-ink">{t}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="pointer-events-none absolute -left-3 -top-3 hidden sm:block">
                        <Chip className="animate-float-soft"><Avatar initials="S" tone="bg-blush text-ink" /> Your clinic</Chip>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

export const FinalCTA = () => (
    <section data-testid="final-cta" className="grain relative overflow-hidden bg-obsidian py-20 lg:py-24">
        <div className="pointer-events-none absolute -right-32 bottom-0 h-80 w-80 rounded-full bg-rose/10 blur-3xl" />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-[1.4fr_1fr] lg:gap-16 lg:px-8">
            <div>
                <Reveal>
                    <h2 className="font-serif text-3xl uppercase leading-[1.1] tracking-tight text-cream sm:text-4xl lg:text-5xl">
                        How many bookings are sitting in your database <span className="italic text-rose">right now?</span>
                    </h2>
                    <p className="mt-5 max-w-xl text-sm leading-relaxed text-[#B9B6C0] sm:text-base">
                        Let’s look at the clients you already have and see where the rebooking opportunities are
                        before you spend more finding new ones.
                    </p>
                    <div className="mt-9">
                        <PrimaryCTA dark testId="final-book-call-btn">Book a Reactivation Call</PrimaryCTA>
                    </div>
                    <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.26em] text-gold">
                        For cosmetic + aesthetic clinics.
                    </div>
                </Reveal>
            </div>
            <Reveal delay={0.15}>
                <div className="rounded-3xl border border-cream/10 bg-matte p-6">
                    <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.2em] text-cream/40">
                        <span>Database</span>
                        <span className="text-gold">→ Booking</span>
                    </div>
                    <div className="relative mt-5">
                        <svg className="absolute left-0 top-[15px] h-1 w-full" preserveAspectRatio="none">
                            <line x1="0" y1="2" x2="100%" y2="2" stroke="#CBA368" strokeOpacity="0.3" strokeWidth="1" className="line-flow" />
                        </svg>
                        <span
                            className="absolute top-[11px] h-2.5 w-2.5 rounded-full bg-gold shadow-[0_0_10px_rgba(203,163,104,0.8)]"
                            style={{ animation: "travelX 4s linear infinite" }}
                        />
                        <div className="relative flex items-start justify-between">
                            {["Sarah", "Elena", "Priya"].map((n, i) => (
                                <div key={n} className="flex flex-col items-center gap-2">
                                    <Avatar initials={n[0]} tone={i === 2 ? "bg-gold/20 text-gold" : "bg-charcoal text-cream/60"} />
                                    <span className="font-mono text-[7px] uppercase tracking-widest text-cream/40">{n}</span>
                                </div>
                            ))}
                            <div className="flex flex-col items-center gap-2">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-cream">
                                    <Check className="h-4 w-4 text-ink" strokeWidth={2.5} />
                                </span>
                                <span className="font-mono text-[7px] uppercase tracking-widest text-gold">Booked ✓</span>
                            </div>
                        </div>
                    </div>
                    <div className="mt-6 border-t border-cream/10 pt-4 text-center font-mono text-[8px] uppercase tracking-[0.22em] text-cream/35">
                        Past clients → follow-up → appointments
                    </div>
                </div>
            </Reveal>
        </div>
    </section>
);
